/**
 * The front door.
 *
 * Rebuilt after the owner's verdict on the previous version: "looks like a
 * scam, not a storage place". That was right, and the diagnosis is worth
 * keeping. Soft pastel gradients, rounded floating cards, a purple everything,
 * big claims about privacy, and no visible product — that is the exact visual
 * grammar of vapour. A real storage product looks *editorial and dense*: an
 * eyebrow label over every section, short heavy headlines, hairline grids
 * instead of floating cards, concrete numbers, black buttons, and colour only
 * inside the product imagery.
 *
 * So: near-black for every action here, the brand purple confined to the drawn
 * application, and a number wherever a number is true. "1.93 GB of 2 GB" does
 * more for credibility than any adjective, because nobody faking it bothers.
 */

import { lazy, Suspense, useEffect, useRef } from "react";
import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
} from "framer-motion";
import { Link } from "@tanstack/react-router";
import { WaitlistForm } from "./WaitlistForm.js";
import { AppWindow } from "./AppWindow.js";
import { WallDemo } from "./WallDemo.js";
import { Logo, PRODUCT_NAME } from "../../components/ui/Logo.js";
import { ScrollProgress } from "./ScrollProgress.js";
import { LocalePicker } from "./LocalePicker.js";
import { type Translate, useT } from "../../lib/i18n.js";
import { Showcase } from "./Showcase.js";
/*
 * Lazy, and for a specific number: the world is 145 KB of path data, a seventh
 * of everything else this page ships. It sits well below the fold, so loading
 * it with the hero would delay the only part anybody sees immediately.
 */
const LandingMap = lazy(() =>
  import("./LandingMap.js").then((m) => ({ default: m.LandingMap })),
);
import { COMPARISON, type Verdict } from "./comparison.js";
import {
  CheckIcon,
  CopyIcon,
  CropIcon,
  CrossIcon,
  ExportIcon,
  FacesIcon,
  KeyIcon,
  LibraryIcon,
  LinkIcon,
  LockIcon,
  PartialIcon,
  PeopleIcon,
  ShieldIcon,
  SlidersIcon,
  VideoIcon,
  WebIcon,
  AndroidIcon,
  PhoneIcon,
  DesktopIcon,
} from "./icons.js";
import "./LandingPage.css";

/** Names the comparison rows use, resolved to the drawn mark. */
const ROW_ICONS = {
  lock: LockIcon,
  library: LibraryIcon,
  sliders: SlidersIcon,
  faces: FacesIcon,
  people: PeopleIcon,
  copy: CopyIcon,
  crop: CropIcon,
  link: LinkIcon,
  export: ExportIcon,
  shield: ShieldIcon,
  video: VideoIcon,
} as const;

/**
 * The tab, in the reader's language.
 *
 * index.html carries the English title and description because that is what a
 * crawler reads — it fetches the HTML and does not press the language button.
 * A Polish reader, though, ends up with an English tab and an English bookmark,
 * which is the one piece of the page the translation could not reach from
 * inside React. Setting it here fixes the visible half; the og: tags stay
 * English on purpose, since nothing that reads them ever runs this code.
 */
function useDocumentTitle(t: Translate) {
  const title = t("MyFriends.Cloud — a shared photo library for your friends");
  const description = t(
    "A shared photo library and private wall for one group of friends. Everyone’s camera roll in one album, encrypted on your own device. No followers, no feed, no strangers.",
  );

  useEffect(() => {
    document.title = title;
    const meta = document.querySelector('meta[name="description"]');
    meta?.setAttribute("content", description);
  }, [title, description]);
}

export function LandingPage() {
  const t = useT();
  useDocumentTitle(t);
  useReveal();

  return (
    <div className="fc-landing">
      <a className="fc-landing__skip" href="#main">
        {t("Skip to content")}
      </a>

      <ScrollProgress />

      <LandingNav />

      <main id="main">
        <Hero />
        <Stats />
        <HowItWorks />
        <Showcase />
        <Capabilities />
        <Comparison />
        {/* After the product, not before it. Somebody has to know what the
            thing is before the argument about why it exists lands. */}
        <Wall />
        <MapSection />
        <Security />
        <Apps />
        <Pricing />
        <Questions />
        <Closing />
      </main>

      <LandingFooter />
    </div>
  );
}

/** The small uppercase label that tells you which section you are in. */
function Eyebrow({ children }: { children: React.ReactNode }) {
  return <span className="fc-landing__eyebrow">{children}</span>;
}

/* ──────────────────────────────── Nav ─────────────────────────────────────── */

function LandingNav() {
  const t = useT();

  return (
    <header className="fc-landing__nav">
      <div className="fc-landing__nav-inner">
        <span className="fc-landing__brand" translate="no">
          <Logo size={22} />
          {PRODUCT_NAME}
        </span>

        <nav className="fc-landing__nav-links" aria-label={t("Page sections")}>
          <a href="#wall">{t("Why")}</a>
          <a href="#how">{t("How it works")}</a>
          <a href="#compare">{t("Compare")}</a>
          <a href="#security">{t("Security")}</a>
          <a href="#pricing">{t("Pricing")}</a>
        </nav>

        <LocalePicker />

        <Link
          className="fc-btn fc-btn--dark"
          to="/login"
          search={{ redirect: undefined }}
        >
          {t("Sign in")}
        </Link>
      </div>
    </header>
  );
}

/* ─────────────────────────────── Hero ─────────────────────────────────────── */

function Hero() {
  const t = useT();
  const still = useReducedMotion();
  const ref = useRef<HTMLElement>(null);

  /*
   * Scroll-linked, not time-linked: the window sinks and shrinks slightly as
   * the hero leaves, so scrolling feels like moving past a thing rather than
   * like a slide changing. CSS scroll-driven animations would do this natively
   * but are not in Safari or Firefox yet, which is what `useScroll` is for.
   *
   * Both transforms start at the identity, so a browser that never runs a
   * frame simply shows the window where it already is.
   */
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, still ? 0 : -56]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, still ? 1 : 0.945]);

  return (
    <section className="fc-landing__hero" ref={ref}>
      <div className="fc-landing__hero-copy" data-reveal>
        <h1>{t("Every photo from the trip, in one place.")}</h1>

        <p className="fc-landing__lead">
          {t(
            "A shared library for one group of friends — everybody’s camera roll in a single album, with a wall to talk about it. Encrypted on your own device before any of it is uploaded.",
          )}
        </p>

        <WaitlistForm tone="hero" />
      </div>

      {/*
        Not aria-hidden any more. It was, when it was a picture — but the
        sidebar became buttons somebody can press, and focusable controls
        inside an aria-hidden container are reachable by keyboard and invisible
        to the screen reader driving it. It is labelled instead.
      */}
      <motion.div
        className="fc-landing__hero-art"
        data-reveal
        role="group"
        aria-label={t("A preview of the app")}
        style={{ y, scale }}
      >
        <AppWindow />
      </motion.div>
    </section>
  );
}

/* ─────────────────────────────── The wall ─────────────────────────────────── */

/**
 * Why this exists, which is not encryption.
 *
 * Encryption is what makes a closed room possible; it is not the reason
 * anybody wants one. The reason is that social networks began small — one
 * school, one year, one group of friends — and grew until the people watching
 * your life were mostly people who barely know you.
 */
function Wall() {
  const t = useT();

  return (
    <section className="fc-landing__wall" id="wall">
      <div className="fc-landing__wall-inner">
        <div data-reveal>
          <Eyebrow>{t("The wall")}</Eyebrow>
          <h2>{t("Social networks used to be small.")}</h2>
        </div>

        <div className="fc-landing__wall-copy" data-reveal>
          <p>
            {t(
              "They began as a closed circle — one school, one year, one group of friends. Then they grew, and kept growing, until the people watching your life were mostly people who barely know you. At some point everybody started editing themselves for an audience, and the ordinary week stopped being worth posting.",
            )}
          </p>
          <p>
            {t(
              "The people who matter did not change. They are the same handful who were actually there — in the photograph at two in the morning, in the joke that needs context, on the trip nobody else would care about.",
            )}
          </p>
          <p className="fc-landing__wall-close">
            {t(
              "So MyFriends.Cloud has a wall, and it goes no further than your group. Post the moment, argue about it underneath, keep the photo with the conversation. There is no algorithm, no reach, and nobody watching who was not invited.",
            )}
          </p>
        </div>

        {/* Inside the grid, not beside it. As a sibling of the grid it could
            not be placed in a row, so it fell below everything and left the
            heading floating alone at the top of a very tall column. */}
        <div className="fc-landing__wall-art" data-reveal>
          <WallDemo />
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────── Stats ────────────────────────────────────── */

/*
 * The three that are genuinely uncapped, plus the free allowance. Verified:
 * no member limit and no file-count limit exist anywhere in the code, and the
 * only ceiling is the storage total.
 */
const STATS = [
  { value: "No limit", label: "on people in a group" },
  { value: "No limit", label: "on photos — only storage" },
  { value: "Full size", label: "originals, never recompressed" },
  { value: "1 GB", label: "free, per group" },
];

function Stats() {
  const t = useT();

  return (
    <section className="fc-landing__stats" aria-label={t("At a glance")}>
      <ul>
        {STATS.map((stat) => (
          <li key={stat.label} data-reveal>
            <strong className="fc-tabular">{t(stat.value)}</strong>
            <span>{t(stat.label)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ───────────────────────────── How it works ───────────────────────────────── */

const STEPS = [
  {
    icon: <PeopleIcon />,
    title: "Make a group",
    body: "Or join one with a code. Your browser makes a key pair and locks the private half with your password.",
  },
  {
    icon: <LibraryIcon />,
    title: "Everyone adds photos",
    body: "Each file is encrypted on the device it came from, before any of it is uploaded. What we store is the encrypted file.",
  },
  {
    icon: <FacesIcon />,
    title: "It sorts itself out",
    body: "Albums, dates, countries, and the people in the pictures — worked out in your browser, not on a server.",
  },
];

function HowItWorks() {
  const t = useT();

  return (
    <section className="fc-landing__how">
      <div className="fc-landing__split">
        <div data-reveal>
          <Eyebrow>{t("How it works")}</Eyebrow>
          <h2>{t("Three steps, then it is out of your way.")}</h2>
        </div>

        <ol className="fc-landing__steps">
          {STEPS.map((step, index) => (
            <li key={step.title} data-reveal>
              <span className="fc-landing__stephead">
                <span className="fc-landing__stepno fc-tabular">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span className="fc-landing__stepicon" aria-hidden="true">
                  {step.icon}
                </span>
              </span>
              <h3>{t(step.title)}</h3>
              <p>{t(step.body)}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* ──────────────────────────── Capability pills ────────────────────────────── */

const PILLS = [
  "Many albums inside one group",
  "Duplicates caught on upload",
  "Slideshow",
  "Faces found on your device",
  "Filter by person, album, year, who added it",
  "Crop, straighten, colour",
  "Comments on every photo",
  "Video transcoding",
  "Trips with countries",
  "Expiring share links",
  "Two-factor sign-in",
  "Offline recovery key",
];

function Capabilities() {
  const t = useT();

  return (
    <section className="fc-landing__pills" aria-label={t("What is included")}>
      <ul>
        {PILLS.map((pill) => (
          <li key={pill} data-reveal>
            {t(pill)}
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ───────────────────────────── Comparison ─────────────────────────────────── */

function Comparison() {
  const t = useT();

  return (
    <section className="fc-landing__compare" id="compare">
      <div className="fc-landing__band-head" data-reveal>
        <Eyebrow>{t("Compared")}</Eyebrow>
        <h2>{t("You already have somewhere to put photos.")}</h2>
        <p>
          {t(
            "Two of them, in fact, and they are good at different halves of this. Here is where each stops.",
          )}
        </p>
      </div>

      {/*
        Below 760px the table is replaced, not squeezed. Three answer columns
        plus a question column needs about 52rem to be readable at all, so on a
        phone it became a 15cm-wide window onto an 83cm-wide table — scrolling
        sideways to read a row, then back to see which row it was. The same ten
        comparisons are rendered as cards there instead.

        Swapped with `display: none`, which takes whichever one is not in use
        out of the accessibility tree as well as off the screen, so a screen
        reader is never offered both.
      */}
      <ComparisonCards />

      <div className="fc-landing__table-scroll" data-reveal>
        <table className="fc-landing__table">
          <caption className="fc-sr-only">
            {t(
              "MyFriends.Cloud compared with a cloud drive and a photo service",
            )}
          </caption>
          <thead>
            <tr>
              <th scope="col">
                <span className="fc-sr-only">{t("Feature")}</span>
              </th>
              {/*
                Ours first, deliberately: the table scrolls sideways on a phone
                and whichever column is last is the one cut off.
              */}
              <th scope="col" data-ours translate="no">
                {PRODUCT_NAME}
              </th>
              <th scope="col">{t("A cloud drive")}</th>
              <th scope="col">{t("A photo service")}</th>
            </tr>
          </thead>
          <tbody>
            {COMPARISON.map((row) => (
              <tr key={row.question}>
                <th scope="row">
                  <span className="fc-landing__rowicon" aria-hidden="true">
                    {(() => {
                      const Mark = ROW_ICONS[row.icon];
                      return <Mark />;
                    })()}
                  </span>
                  {t(row.question)}
                </th>
                <Cell {...row.ours} ours />
                <Cell {...row.drive} />
                <Cell {...row.photos} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * The same ten comparisons, for a screen too narrow to hold a table.
 *
 * A list of sections rather than a table: each comparison is a heading with
 * three labelled answers under it, which is the shape the information actually
 * has once the columns are gone. The column name has to be repeated in every
 * card — with no header row above it, "Yes — no count limit" belongs to
 * nobody.
 */
function ComparisonCards() {
  const t = useT();

  return (
    <ul className="fc-landing__compare-cards">
      {COMPARISON.map((row) => {
        const Mark = ROW_ICONS[row.icon];
        return (
          <li key={row.question} data-reveal>
            <h3>
              <span className="fc-landing__rowicon" aria-hidden="true">
                <Mark />
              </span>
              {t(row.question)}
            </h3>

            <CardAnswer who={PRODUCT_NAME} {...row.ours} ours />
            <CardAnswer who={t("A cloud drive")} {...row.drive} />
            <CardAnswer who={t("A photo service")} {...row.photos} />
          </li>
        );
      })}
    </ul>
  );
}

function CardAnswer({
  who,
  verdict,
  note,
  ours,
}: {
  who: string;
  verdict: Verdict;
  note: string;
  ours?: boolean;
}) {
  const t = useT();
  const Mark =
    verdict === "yes"
      ? CheckIcon
      : verdict === "partial"
        ? PartialIcon
        : CrossIcon;

  return (
    <div
      className="fc-landing__compare-answer"
      data-verdict={verdict}
      data-ours={ours || undefined}
    >
      <span
        className="fc-landing__compare-who"
        translate={ours ? "no" : undefined}
      >
        {who}
      </span>
      <span className="fc-landing__verdict">
        <Mark />
        <span className="fc-sr-only">
          {verdict === "yes"
            ? t("Yes")
            : verdict === "partial"
              ? t("Partly")
              : t("No")}
          .{" "}
        </span>
      </span>
      <span className="fc-landing__compare-note">{t(note)}</span>
    </div>
  );
}

/**
 * One answer.
 *
 * The mark and the sentence say the same thing on purpose. A tick alone is a
 * colour-and-shape claim that means nothing to a screen reader, so the verdict
 * is also a word in the accessible name.
 */
function Cell({
  verdict,
  note,
  ours,
}: {
  verdict: Verdict;
  note: string;
  ours?: boolean;
}) {
  const t = useT();

  const Mark =
    verdict === "yes"
      ? CheckIcon
      : verdict === "partial"
        ? PartialIcon
        : CrossIcon;
  const label =
    verdict === "yes"
      ? t("Yes")
      : verdict === "partial"
        ? t("Partly")
        : t("No");

  return (
    <td data-verdict={verdict} data-ours={ours || undefined}>
      <span className="fc-landing__verdict">
        <Mark />
        <span className="fc-sr-only">{label}. </span>
      </span>
      <span>{t(note)}</span>
    </td>
  );
}

/* ─────────────────────────────── The map ──────────────────────────────────── */

/**
 * Where a group has been, drawing itself.
 *
 * The same map the application shows, running one summer at a time. It earns
 * its place by being the one feature on this page that is genuinely nice to
 * look at rather than useful to read about — and by being real: it is the same
 * component, the same shapes and the same colour as the thing you get.
 */
function MapSection() {
  const t = useT();

  return (
    <section className="fc-landing__map" id="map">
      <div className="fc-landing__split">
        <div data-reveal>
          <Eyebrow>{t("The map")}</Eyebrow>
          <h2>{t("Every summer, on one map.")}</h2>
          <p className="fc-landing__body">
            {t(
              "Name the countries when you make an album and the map fills itself in — including the ones a trip only passed through, because an album can name several. Add a country you never made an album for, or stop counting one; it is your map either way.",
            )}
          </p>
        </div>

        <div className="fc-landing__map-art" data-reveal>
          {/* A box of the right shape, so the page does not jump when it lands. */}
          <Suspense
            fallback={
              <div className="fc-landing__map-loading" aria-hidden="true" />
            }
          >
            <LandingMap />
          </Suspense>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────── Security ─────────────────────────────────── */

const GUARANTEES = [
  {
    icon: <LockIcon />,
    title: "Nobody outside your group",
    body: "Every photo has its own key, sealed to the group. AES-256-GCM.",
  },
  {
    icon: <KeyIcon />,
    title: "Not us either",
    body: "The key is made on your device. Files are encrypted there, before they are uploaded.",
  },
  {
    icon: <ShieldIcon />,
    title: "Your password is the key",
    body: "600,000 rounds of PBKDF2 between the two, and we never see either.",
  },
  {
    icon: <FacesIcon />,
    title: "Faces never leave your phone",
    body: "Recognition runs in your browser, on pictures it already has open.",
  },
  {
    icon: <LinkIcon />,
    title: "A link you can take back",
    body: "Its key rides in the part of the URL a server never receives. Expire it whenever.",
  },
  {
    icon: <ExportIcon />,
    title: "Leave with everything",
    body: "Download every original at any time. No queue, nobody to ask.",
  },
];

function Security() {
  const t = useT();

  return (
    <section className="fc-landing__security" id="security">
      <div className="fc-landing__band-head" data-reveal>
        <Eyebrow>{t("Security")}</Eyebrow>
        <h2>{t("Encrypted before it leaves your device.")}</h2>
        <p>
          {t(
            "Not a policy we could quietly change — an arrangement where the server is never handed anything that opens a photograph.",
          )}
        </p>
      </div>

      <ul className="fc-landing__cards">
        {GUARANTEES.map((item) => (
          <li key={item.title} data-reveal>
            <span className="fc-landing__cardicon" aria-hidden="true">
              {item.icon}
            </span>
            <h3>{t(item.title)}</h3>
            <p>{t(item.body)}</p>
          </li>
        ))}
      </ul>

      <p className="fc-landing__note" data-reveal>
        {t(
          "The exact limits, including how a group can keep an offline copy of its key, are set out in the",
        )}{" "}
        <Link to="/privacy">{t("privacy policy")}</Link>.
      </p>
    </section>
  );
}

/* ─────────────────────────────── Apps ─────────────────────────────────────── */

/**
 * Where you can run it, and what you can do about it today.
 *
 * Only the first one has anywhere to send you, so only the first one is a
 * link. The other three are disabled buttons rather than links to a page that
 * would say "not yet" in more words — a control that cannot do anything should
 * look like it, and `aria-disabled` with the word "Soon" beside it says so to
 * a screen reader as well as to an eye.
 */
const PLATFORMS = [
  {
    icon: <WebIcon />,
    name: "Web",
    note: "Available now, every browser",
    action: "Open it",
  },
  {
    icon: <AndroidIcon />,
    name: "Android",
    note: "Being built now",
    action: null,
  },
  { icon: <PhoneIcon />, name: "iOS", note: "Planned", action: null },
  { icon: <DesktopIcon />, name: "Desktop", note: "Planned", action: null },
];

function Apps() {
  const t = useT();

  return (
    <section className="fc-landing__apps">
      <div className="fc-landing__split">
        <div data-reveal>
          <Eyebrow>{t("Apps")}</Eyebrow>
          <h2>{t("Everyone is invited, whatever they carry.")}</h2>
          <p className="fc-landing__body">
            {t(
              "Open it in the browser and add it to your home screen — it behaves like an app, offline cache and all. iPhone, Android, Windows, Mac — one group, everybody in it, nobody left out because of the phone they bought. A native Android app is being built; iOS and desktop come after it.",
            )}
          </p>
        </div>

        <ul className="fc-landing__platforms">
          {PLATFORMS.map((platform) => (
            <motion.li
              key={platform.name}
              data-reveal
              whileHover={{ y: -4 }}
              transition={{ type: "spring", stiffness: 420, damping: 26 }}
            >
              <span className="fc-landing__platicon" aria-hidden="true">
                {platform.icon}
              </span>
              <strong>{t(platform.name)}</strong>
              <span>{t(platform.note)}</span>

              {platform.action ? (
                <Link
                  className="fc-landing__platbtn"
                  to="/login"
                  search={{ redirect: undefined }}
                >
                  {t(platform.action)}
                </Link>
              ) : (
                <span className="fc-landing__platbtn" data-soon>
                  {t("Soon")}
                </span>
              )}
            </motion.li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ─────────────────────────────── Pricing ──────────────────────────────────── */

const TIERS = [
  {
    name: "Free",
    price: "€0",
    cadence: "",
    storage: "1 GB per group",
    lines: [
      "2 groups of your own",
      "Join any number of groups",
      "Every feature",
    ],
  },
  {
    name: "Plus",
    price: "€2.99",
    cadence: "/mo",
    storage: "100 GB",
    highlight: true,
    lines: ["10 groups of your own", "Longer videos", "Originals kept forever"],
  },
  {
    name: "Circle",
    price: "€7.99",
    cadence: "/mo",
    storage: "300 GB",
    lines: [
      "25 groups of your own",
      "Unlimited share links",
      "Originals over links",
    ],
  },
  {
    name: "Vault",
    price: "€24.99",
    cadence: "/mo",
    storage: "1 TB",
    lines: ["Unlimited groups", "Versioned storage", "Everything in Circle"],
  },
];

function Pricing() {
  const t = useT();

  return (
    <section className="fc-landing__pricing" id="pricing">
      <div className="fc-landing__band-head" data-reveal>
        <Eyebrow>{t("Pricing")}</Eyebrow>
        <h2>{t("Free to try. Free to stay.")}</h2>
        <p>
          {t(
            "Storage is the only thing that costs us anything, so it is the only thing we charge for. Every feature is in every plan.",
          )}
        </p>
      </div>

      <ul className="fc-landing__tiers">
        {TIERS.map((tier) => (
          <motion.li
            key={tier.name}
            data-reveal
            data-highlight={tier.highlight || undefined}
            whileHover={{ y: -6 }}
            transition={{ type: "spring", stiffness: 340, damping: 28 }}
          >
            <h3>{tier.name}</h3>
            <p className="fc-landing__price">
              <span className="fc-tabular">{tier.price}</span>
              {tier.cadence && <span>{tier.cadence}</span>}
            </p>
            <p className="fc-landing__storage">{t(tier.storage)}</p>
            <ul>
              {tier.lines.map((line) => (
                <li key={line}>{t(line)}</li>
              ))}
            </ul>
          </motion.li>
        ))}
      </ul>

      <p className="fc-landing__note" data-reveal>
        {t(
          "Paid plans are not switched on yet and these numbers may still move. The free plan is real, and is what everybody is on today.",
        )}
      </p>
    </section>
  );
}

/* ────────────────────────────── Questions ─────────────────────────────────── */

const QUESTIONS = [
  {
    q: "Why can I not just sign up?",
    a: "Every group costs storage and every account is somebody we would rather be able to help properly. Invitations go out in batches so the second part stays true.",
  },
  {
    q: "What if I forget my password?",
    a: "If your group has a recovery copy of its key, you can be let back in. If it does not, the photographs stay locked — which is the same property that keeps them private.",
  },
  {
    q: "Can I get my photos out?",
    a: "At any time, as the original files, without asking anybody. There is no export queue and nothing to wait for.",
  },
  {
    q: "What happens when I leave a group?",
    a: "You lose access immediately. Anything you uploaded stays with the group unless you delete it first — the others hold keys to it, and it is as much their trip as yours.",
  },
];

function Questions() {
  const t = useT();

  return (
    <section className="fc-landing__faq">
      <div className="fc-landing__split">
        <div data-reveal>
          <Eyebrow>{t("Questions")}</Eyebrow>
          <h2>{t("The ones people actually ask.")}</h2>
        </div>

        <dl className="fc-landing__questions">
          {QUESTIONS.map((item) => (
            <div key={item.q} data-reveal>
              <dt>{t(item.q)}</dt>
              <dd>{t(item.a)}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

/* ─────────────────────────────── Closing ──────────────────────────────────── */

function Closing() {
  const t = useT();

  return (
    <section className="fc-landing__closing">
      <div data-reveal>
        <h2>{t("There will be room soon.")}</h2>
        <p>
          {t(
            "Leave an address and we will write when there is. One email — no newsletter, no product updates, no reminders.",
          )}
        </p>
        <WaitlistForm tone="closing" />
      </div>
    </section>
  );
}

/* ─────────────────────────────── Footer ───────────────────────────────────── */

function LandingFooter() {
  const t = useT();

  return (
    <footer className="fc-landing__footer">
      <div className="fc-landing__footer-inner">
        <span className="fc-landing__brand" translate="no">
          <Logo size={22} />
          {PRODUCT_NAME}
        </span>

        <nav aria-label={t("Legal and account")}>
          <Link to="/privacy">{t("Privacy")}</Link>
          <Link to="/terms">{t("Terms")}</Link>
          <Link to="/login" search={{ redirect: undefined }}>
            {t("Sign in")}
          </Link>
        </nav>

        <p className="fc-landing__copyright">
          {t("Encrypted on your devices, before anything is uploaded.")}
        </p>
      </div>
    </footer>
  );
}

/* ─────────────────────────────── Motion ───────────────────────────────────── */

/**
 * Reveal sections as they arrive, once each.
 *
 * An observer rather than a scroll handler: the browser does the work and there
 * is nothing to throttle. Everything is visible without this — `[data-reveal]`
 * only becomes hidden inside `prefers-reduced-motion: no-preference`, so a
 * reader who asked for less motion, or anybody whose JavaScript failed, gets
 * the whole page rather than a blank screen.
 */
function useReveal() {
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;

    const targets = document.querySelectorAll("[data-reveal]");
    if (!("IntersectionObserver" in window)) {
      targets.forEach((el) => el.setAttribute("data-shown", ""));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.setAttribute("data-shown", "");
          observer.unobserve(entry.target);
        }
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.05 },
    );

    targets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

/* ─────────────────────────────── Marks ────────────────────────────────────── */

/* Referenced so the icon set stays one import for the whole page. */
void PeopleIcon;
void CropIcon;
